import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { content } from 'src/assets/content/fallback-content';

import { GatekeeperService } from '../../shared/services/gatekeeper/gatekeeper.service';
import { SharedInfoService } from '../../shared/services/shared-info/shared-info.service';

@Component({
  selector: 'rcc-pdf-report-info',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './rcc-pdf-report-info.component.html',
  styleUrl: './rcc-pdf-report-info.component.scss',
})
export class RccPdfReportInfoComponent {
  private readonly stateNameMap: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AS: 'American Samoa',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FM: 'Federated States of Micronesia',
    FL: 'Florida',
    GA: 'Georgia',
    GU: 'Guam',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MH: 'Marshall Islands',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    MP: 'Northern Mariana Islands',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PW: 'Palau',
    PA: 'Pennsylvania',
    PR: 'Puerto Rico',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VI: 'U.S. Virgin Islands',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
  };

  @Input() printClientName = '';

  content = content.Data.v4_rcc_root.content.pdfreport;
  clientInfoTaxContent = content.Data.v4_rcc_root.content.clientInfoTax;
  rothConvDetailsContent = content.Data.v4_rcc_root.content.rothConvDetails;
  stateTaxRatesFromBXR: boolean;

  constructor(
    private readonly contentService: ContentService,
    private readonly gatekeeperService: GatekeeperService,
    public readonly clientInfo: SharedInfoService,
  ) {
    this.contentService.getContent().subscribe((data) => {
      this.content = data.Data.v4_rcc_root.content.pdfreport;
      this.clientInfoTaxContent = data.Data.v4_rcc_root.content.clientInfoTax;
      this.rothConvDetailsContent = data.Data.v4_rcc_root.content.rothConvDetails;
    });
    this.stateTaxRatesFromBXR = this.gatekeeperService.getToggleValue('STATE_TAX_RATES');
  }

  get printDate(): string {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  get inputData() {
    return this.clientInfo.currentInputData;
  }

  get currentFilingStatusLabel(): string {
    return this.getFilingStatusLabel(this.inputData.currentFilingStatus);
  }

  get futureFilingStatusLabel(): string {
    return this.getFilingStatusLabel(this.inputData.futureFilingStatus);
  }

  get paymentMethodLabel(): string {
    const method = this.inputData.howWillYouPay;
    if (method === 'cash') return this.rothConvDetailsContent?.cash;
    if (method === 'ira') return this.rothConvDetailsContent?.ira;
    if (method === 'tax-efficient') return this.rothConvDetailsContent?.taxEfficient;
    if (method === 'tax-inefficient') return this.rothConvDetailsContent?.taxInefficient;
    return method;
  }

  get showTaxableAccountFields(): boolean {
    return this.inputData.howWillYouPay === 'tax-efficient';
  }

  get showCashFields(): boolean {
    return this.inputData.howWillYouPay === 'cash';
  }

  getPercent(value: number): string {
    return value + '%';
  }

  getStateDisplayName(stateCode: string): string {
    return this.stateNameMap[stateCode] ?? stateCode;
  }

  private getFilingStatusLabel(status: string): string {
    const map: Record<string, string> = {
      single: this.clientInfoTaxContent?.single,
      'married-filing-jointly': this.clientInfoTaxContent?.marriedjointly,
      'married-filing-separately': this.clientInfoTaxContent?.marriedseparately,
      'head-of-household': this.clientInfoTaxContent?.headofhouseholds,
    };
    return map[status] ?? status;
  }
}
